import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function TinderForRestaurantsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Tinder for Restaurants — Swipe to Decide Where to Eat | Hangrily"
        description="Looking for a Tinder-like app for restaurants? Hangrily lets you swipe through nearby restaurants with real photos & ratings. Match with your partner or group on places you all want to eat. The original swipe-based restaurant picker. Free to try."
        path="/tinder-for-restaurants"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Tinder for Restaurants - Hangrily',
            description: 'Hangrily is the Tinder-like restaurant decision app. Swipe right on restaurants you want, left on ones you don\'t. Match with your partner or group.',
            url: 'https://hangrily.app/tinder-for-restaurants'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Tinder for Restaurants', item: 'https://hangrily.app/tinder-for-restaurants' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Is there a Tinder for restaurants?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! Hangrily is the original "Tinder for restaurants." You swipe right on restaurants you want to try, left on ones you don\'t. If you\'re deciding with your partner or group, everyone swipes independently and the app matches you on restaurants you all liked. It uses real restaurant data with photos, ratings, prices, and distance.'
                }
              },
              {
                '@type': 'Question',
                name: 'What app lets you swipe on restaurants like Tinder?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily is the top-rated app for swiping on restaurants. It works just like Tinder: see a restaurant card with photos and info, swipe right to like or left to pass. But it goes further with Couple Mode (both partners swipe and match), Group Mode (up to 10 friends), and smart preference filters for cuisine, price, and mood.'
                }
              },
              {
                '@type': 'Question',
                name: 'How does Hangrily compare to actual Tinder?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Same addictive swipe UX, completely different purpose. Where Tinder matches you with people, Hangrily matches you with restaurants. You see photos, ratings, cuisine type, price level, and distance for each restaurant. Swipe right to like, left to pass, or swipe up to love. In Couple Mode, you only match on restaurants you both swiped right on.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I use the restaurant Tinder app with my partner?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely — that\'s Hangrily\'s most popular feature. In Couple Mode, both partners swipe through the same restaurants independently. Neither person sees the other\'s choices until both finish. Then the app reveals mutual matches. It completely eliminates the "where do you want to eat" argument.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is the restaurant Tinder app free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily offers a free trial with no account required. You get 3 free sessions per week. Premium is $4.99/month for unlimited sessions, analytics, favorites, and rewards.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Tinder for Restaurants</h1>
          <p className="hero-subtitle">
            Swipe right on dinner. The restaurant decision app that works like Tinder — but for food.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>Yes, There's a Tinder for Restaurants. It's Called Hangrily.</h2>
              <p>
                You know the feeling: you're hungry, you open Google Maps, and you're hit with 50+ restaurant 
                options. Your eyes glaze over. Your partner says "I don't care, you pick." 45 minutes later, 
                you're still deciding.
              </p>
              <p>
                <strong>Hangrily fixes this with the same swipe interface that made Tinder addictive</strong> — 
                but instead of people, you're swiping through restaurants. See a restaurant card with real photos, 
                ratings, cuisine type, price level, and distance. Swipe right if it looks good, left if it doesn't. 
                Done in 2 minutes.
              </p>
              <p>
                And here's the magic: if you're deciding with your partner or a group of friends, everyone swipes 
                independently. <strong>You only match on restaurants everyone liked.</strong> No arguments. No vetoes. 
                Just consensus.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Start Swiping Now — Free →
              </button>
            </div>
            <div className="column-visual">
              <div className="stat-highlight-grid">
                <div className="stat-highlight">
                  <span className="stat-number-lg">2 min</span>
                  <span className="stat-label-sm">Average decision time</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">15+</span>
                  <span className="stat-label-sm">Restaurant cards per session</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">3</span>
                  <span className="stat-label-sm">Swipe options: Like, Love, Pass</span>
                </div>
                <div className="stat-highlight">
                  <span className="stat-number-lg">10</span>
                  <span className="stat-label-sm">Max group size</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">How the Restaurant Tinder App Works</h2>
          <div className="numbered-steps">
            <div className="numbered-step">
              <div className="step-num">1</div>
              <div className="step-content">
                <h3>Set Your Cravings</h3>
                <p>
                  Pick your cuisine preferences (Italian, Mexican, Thai, Sushi, BBQ, and more), set your price range 
                  ($-$$$$), choose your distance limit, and select your mood. Hangrily uses your GPS to find real 
                  restaurants near you — not random chains from across town.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">2</div>
              <div className="step-content">
                <h3>Swipe Like You Mean It</h3>
                <p>
                  Restaurant cards appear one at a time — just like Tinder. Each card shows the restaurant photo, 
                  name, rating, cuisine type, price level, and distance. <strong>Swipe right</strong> to like, 
                  <strong> swipe left</strong> to pass, or <strong>swipe up</strong> to love (gives extra weight). 
                  Your partner or group swipes too — independently, no peeking.
                </p>
              </div>
            </div>
            <div className="numbered-step">
              <div className="step-num">3</div>
              <div className="step-content">
                <h3>It's a Match!</h3>
                <p>
                  Our algorithm processes everyone's swipes through up to 3 rounds, narrowing 15+ restaurants down 
                  to the one you all agree on. You get the winner, plus runner-ups, distance info, and a link to 
                  navigate there. The whole thing takes about 2 minutes.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Three Modes for Every Situation</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">🍽️</span>
              <h3>Solo Mode</h3>
              <p>Can't decide what to eat alone? Swipe through options and let the algorithm pick your top match. Faster than scrolling Yelp for 20 minutes.</p>
              <Link to="/where-should-i-eat" className="use-case-link">Where Should I Eat? →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💑</span>
              <h3>Couple Mode</h3>
              <p>The killer feature. Both of you swipe independently. You only match on restaurants you BOTH liked. No more "I don't care, you pick" conversations.</p>
              <Link to="/couples-restaurant-picker" className="use-case-link">Couples Picker →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👥</span>
              <h3>Group Mode</h3>
              <p>Up to 10 friends join via code. Everyone swipes, and the app finds the restaurant with the most consensus. Perfect for group dinners and team lunches.</p>
              <Link to="/group-restaurant-decision" className="use-case-link">Group Decisions →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🌙</span>
              <h3>Date Night</h3>
              <p>Turn restaurant picking into a fun mini-game with your partner. Both swipe, reveal your matches, and head out. Makes date night planning part of the fun.</p>
              <Link to="/date-night-restaurants" className="use-case-link">Date Night Mode →</Link>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Hangrily vs. Other Restaurant Apps</h2>
          <div className="comparison-grid">
            <div className="comparison-item lose">
              <h3>❌ Google Maps / Yelp</h3>
              <p>Shows you a map with 100 pins. No swiping, no matching, no group voting. You scroll endlessly and still can't decide.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ Wheel Spinners / Roulette</h3>
              <p>Randomly picks from a list. No preference matching, no real restaurant data, no couple or group support. Pure chaos.</p>
            </div>
            <div className="comparison-item lose">
              <h3>❌ DoorDash / Uber Eats</h3>
              <p>Great for delivery, but the browsing experience causes decision fatigue. You spend 30 minutes scrolling menus instead of just picking a place.</p>
            </div>
            <div className="comparison-item win">
              <h3>✅ Hangrily</h3>
              <p>Tinder-style swiping with real data. Preference-filtered, location-based, couple & group matching. Decision made in 2 minutes. The original restaurant Tinder.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is there a Tinder for restaurants?</h3>
              <p>Yes! Hangrily is the original "Tinder for restaurants." Swipe right on restaurants you want to try, left on ones you don't. Match with your partner or group on places everyone liked.</p>
            </div>
            <div className="faq-item">
              <h3>What app lets you swipe on restaurants?</h3>
              <p>Hangrily uses the same swipe interface as Tinder but for choosing restaurants. See photos, ratings, and info for each restaurant, then swipe to decide. Works solo, with a partner, or with up to 10 friends.</p>
            </div>
            <div className="faq-item">
              <h3>Can I use it with my partner?</h3>
              <p>That's the most popular feature. Couple Mode lets both partners swipe independently. You only match on restaurants you both liked. No more dinner arguments.</p>
            </div>
            <div className="faq-item">
              <h3>Is the restaurant Tinder app free?</h3>
              <p>Yes — try it free with no account needed. Get 3 free sessions per week. Premium is just $9.99/month for unlimited sessions.</p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Ready to Swipe Right on Dinner?</h2>
          <p>Join thousands who've found their perfect restaurant match. Try free — no account needed.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Start Swiping Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
